import { useUpload } from '@web/react-common'
import { useLocalStorageState } from 'ahooks'
import { message } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { LocalFileFragment, useFileCreateMutation } from '@/generated/graphql'

import { FileState, emptyFileState } from './file-upload.state'

export const useFileUpload = ({
  name,
  currentFiles,
  onStart,
  onProgress,
  onComplete,
  onError,
  onValidation,
}: {
  name: string
  currentFiles?: LocalFileFragment[]
  onStart?: (file?: LocalFileFragment | null) => void
  onProgress?: (file?: LocalFileFragment | null) => void
  onComplete?: (file?: LocalFileFragment | null) => void
  onError?: (file?: LocalFileFragment | null) => void
  onValidation?: (file: File) => { errorInfo: string; errorMessage: string }
}) => {
  const TaleFileStateVersion = name

  const { t } = useTranslation()
  const [upload] = useFileCreateMutation()

  const [files, setFiles] = React.useState<FileState>({
    files: currentFiles ? [...currentFiles] : [],
  })

  const [state, setState] = currentFiles
    ? [files, setFiles]
    : useLocalStorageState<FileState>(TaleFileStateVersion, emptyFileState)

  const { uploadClickRef } = useUpload(
    {
      upload: async (file) => {
        const { data } = await upload({
          variables: {
            file,
          },
        })
        return data!.fileCreate
      },
      onUploadValidation(file) {
        return onValidation ? onValidation(file) : { errorInfo: '', errorMessage: '' }
      },
      onUploadStart(file) {
        setState((state: any) => ({ files: [...state!.files, file] }))
        onStart?.(file)
      },
      onUploadProgress(file) {
        setState((state: any) => ({ files: state!.files.map((f: any) => (f.fileId === file.fileId ? file : f)) }))
        onProgress?.(file)
      },
      onUploadComplete(file) {
        setState((state: any) => ({ files: state!.files.map((f: any) => (f.fileId === file.fileId ? file : f)) }))
        onComplete?.()
      },
      onUploadError(file) {
        message.error(t('post.editor.uploadFailed'))
        if (file) {
          setState((state: any) => ({ files: state!.files.filter((f: any) => f.fileId !== file.fileId) }))
        }
        onError?.(file)
      },
    },
    []
  )

  return { state, setState, uploadClickRef }
}
